/**
 * PublicDiagramView — Iter Feb 2026 R39 · R47.
 * Anonymous, read-only view of a diagram via its public share token.
 * R47: renders node images (fill/below) + click-to-lightbox.
 */
import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { ReactFlow, Background, Controls, MiniMap, Handle, Position } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import axios from "axios";
import { Workflow, Lock } from "lucide-react";
import ImageLightbox from "@/components/diagrams/ImageLightbox";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function primaryImage(atts = []) {
  return (atts || []).find(a => a?.kind === "image" && (a?.display === "fill" || a?.display === "below")) || null;
}

function NodeShell({ data }) {
  const shape = data?.shape || "process";
  const color = data?.color || "#0ea5e9";
  const img = primaryImage(data?.attachments);
  const onImgClick = (e) => {
    e.stopPropagation();
    data?.onImageClick?.(img?.url, img?.label);
  };
  if (shape === "decision") {
    return (
      <div className="relative w-[140px] h-[80px]" data-testid="public-node">
        <Handle type="target" position={Position.Left} />
        <div className="absolute inset-0 flex items-center justify-center"
              style={{ background: "#fff", border: `2px solid ${color}`, transform: "rotate(45deg)" }} />
        {img?.display === "fill" && (
          <img src={img.url} alt={img.label || ""} onClick={onImgClick}
                className="absolute inset-2 w-[calc(100%-16px)] h-[calc(100%-16px)] object-cover cursor-zoom-in select-none"
                style={{ transform: "rotate(-45deg)", clipPath: "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)" }}
                data-testid="public-node-image" />
        )}
        <div className="absolute inset-0 flex items-center justify-center text-[12px] font-medium px-2 text-center pointer-events-none">
          <span className="whitespace-pre-wrap break-words">{data?.label || "Decision"}</span>
        </div>
        {img?.display === "below" && (
          <img src={img.url} alt={img.label || ""} onClick={onImgClick}
                className="absolute left-1/2 -translate-x-1/2 top-full mt-2 max-w-[180px] max-h-[120px] object-contain border bg-white shadow-sm cursor-zoom-in"
                data-testid="public-node-image" />
        )}
        <Handle type="source" position={Position.Right} />
      </div>
    );
  }
  const rounded = shape === "start" || shape === "end" ? "rounded-full" : "rounded-md";
  return (
    <div className={`relative text-[13px] font-medium px-3 py-2 min-w-[110px] text-center bg-white ${rounded}`}
          style={{ border: `2px solid ${color}` }} data-testid="public-node">
      <Handle type="target" position={Position.Left} />
      {img?.display === "fill" && (
        <img src={img.url} alt={img.label || ""} onClick={onImgClick}
              className="block mx-auto mb-1 max-w-[220px] max-h-[140px] w-full object-cover cursor-zoom-in select-none"
              data-testid="public-node-image" />
      )}
      {data?.label || "Node"}
      {img?.display === "below" && (
        <img src={img.url} alt={img.label || ""} onClick={onImgClick}
              className="absolute left-1/2 -translate-x-1/2 top-full mt-2 max-w-[220px] max-h-[140px] object-contain border bg-white shadow-sm cursor-zoom-in"
              data-testid="public-node-image" />
      )}
      <Handle type="source" position={Position.Right} />
    </div>
  );
}
const nodeTypes = { flow: NodeShell };

export default function PublicDiagramView() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const openLightbox = useCallback((src, alt) => { if (src) setLightbox({ src, alt }); }, []);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/v1/public/diagrams/${token}`)
      .then(r => setD(r.data))
      .catch(e => setErr(e?.response?.data?.detail || "Not shared or link revoked"));
  }, [token]);
  if (err) {
    return (
      <div className="h-screen flex items-center justify-center flex-col gap-2 text-center p-6">
        <Lock className="w-8 h-8 text-neutral-400" />
        <div className="font-semibold">Diagram unavailable</div>
        <div className="text-sm text-muted-foreground">{err}</div>
      </div>
    );
  }
  if (!d) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  const rfNodes = (d.nodes || []).map(n => ({
    ...n, type: "flow", draggable: false, connectable: false,
    data: { ...(n.data || {}), onImageClick: openLightbox },
  }));
  return (
    <div className="h-screen flex flex-col" data-testid="public-diagram-view">
      <div className="border-b bg-background px-4 py-3 flex items-center gap-3">
        <Workflow className="w-5 h-5" />
        <div className="min-w-0">
          <div className="font-semibold truncate">{d.title}</div>
          {d.description && <div className="text-xs text-muted-foreground truncate">{d.description}</div>}
        </div>
        <div className="flex-1" />
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground border px-2 py-1">Read-only · Public link</div>
      </div>
      <div className="flex-1">
        <ReactFlow
          nodes={rfNodes}
          edges={d.edges || []}
          nodeTypes={nodeTypes}
          fitView
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
        >
          <Background gap={20} size={1} color="#e5e7eb" />
          <Controls showInteractive={false} />
          <MiniMap pannable zoomable />
        </ReactFlow>
      </div>
      <ImageLightbox src={lightbox?.src} alt={lightbox?.alt} onClose={() => setLightbox(null)} />
    </div>
  );
}
